<template>
  <div>
    <v-modal :short="isSuccess" @close="handleClose">
      <template slot="#post-creator">
        <action-body class="post-creator__container">
          <div v-if="isSuccess" class="p-3 pt-5 mt-3 container text-center">
            <v-icon
              icon="done_all"
              size="3rem"
              color="var(--primary-brand-color-hex)"
              class="mt-5"
            />
            <h2 class="text-dark-blue mb-0 mt-1">
              {{
                $t("components.messageThread.postCreatorModal.postPublishTitle")
              }}
            </h2>
            <p class="text-grey-70 mt-2">
              {{
                $t(
                  "components.messageThread.postCreatorModal.postPublishSuccess"
                )
              }}
            </p>
          </div>
          <template v-else>
            <v-input
              v-model="title"
              flush
              :placeholder="
                $t(
                  'components.messageThread.postCreatorModal.postTitlePlaceHolder'
                )
              "
              class="post-title mx-2 pt-3"
            />
            <rich-text-editor
              :placeholder="
                $t(
                  'components.messageThread.postCreatorModal.writeSomethingPlaceHolder'
                )
              "
              :content="content"
              :uploaded-files="uploadedFiles"
              :attachments="visibleAttachments"
              class="px-2 pb-2"
              @input="value => (content = value)"
              @uploading="value => (isUploading = value)"
              @uploaded="handleUploaded"
              @removeUploaded="handleRemoveUploaded"
              @removeAttachment="handleRemoveAttachment"
            />
          </template>
        </action-body>
        <action-footer classes="w-100 d-flex post-creator__footer">
          <v-button ghost class="w-25 p-0" @click="handleClose">
            {{
              $t(
                "components.messageThread.postCreatorModal.postPublishButtonClose"
              )
            }}
          </v-button>
          <v-button
            v-if="!isSuccess"
            class="w-75 publish-button"
            :is-loading="isPosting"
            :disabled="isDisableSubmit"
            @click="handlePublishPost"
          >
            {{
              $t(
                "components.messageThread.postCreatorModal.postPublishButtonPublish"
              )
            }}
          </v-button>
        </action-footer>
      </template>
    </v-modal>

    <alert-dialog
      :actions="[
        {
          title: $t(
            'components.messageThread.postCreatorModal.dialogUnsavedConfirmCancel'
          ),
          callback: () => (this.showExitModal = false)
        },
        {
          title: $t(
            'components.messageThread.postCreatorModal.dialogUnsavedConfirmYes'
          ),
          callback: () => $emit('close')
        }
      ]"
      :show="showExitModal"
      :title="
        $t('components.messageThread.postCreatorModal.dialogUnsavedTitle')
      "
      centered
      @close="() => (this.showExitModal = false)"
    >
      <p class="font-semi-bold">
        {{
          $t("components.messageThread.postCreatorModal.messageUnsavedChanges")
        }}
      </p>
    </alert-dialog>
  </div>
</template>

<script>
import ApiClient from "@/lib/api-client"
import debounce from "@/lib/debounce"
import { urlCompare } from "@/lib/url-helpers"
import { isEmpty, isPresent } from "@/lib/utils"
import { get, set, unset } from "@/lib/local-storage"
import { extractUrls } from "@/lib/messages-utils"

import ActionBody from "@/components/modals/shared/ActionBody"
import ActionFooter from "@/components/modals/shared/ActionFooter"

import vInput from "@/components/forms/vInput"
import RichTextEditor from "@/components/RichTextEditor"

export default {
  components: {
    ActionBody,
    ActionFooter,
    vInput,
    RichTextEditor,
    AlertDialog: () => import("@/components/AlertDialog")
  },

  props: {
    messageThreadId: { type: [Number, String], required: true }
  },

  data() {
    return {
      title: null,
      content: null,
      uploadedFiles: [],
      extractedUrls: [],
      ignoredUrls: [],
      fetchedAttachments: [],

      isUploading: false,
      isPosting: false,
      isSuccess: false,
      showExitModal: false
    }
  },

  computed: {
    isDisableSubmit() {
      return (
        this.isUploading ||
        (isEmpty(this.content) && this.uploadedFiles.length === 0)
      )
    },

    isDirty() {
      return (
        isPresent(this.title) ||
        isPresent(this.content) ||
        isPresent(this.uploadedFiles)
      )
    },

    draftKey() {
      return `mvt-post-draft:{${this.messageThreadId}}`
    },

    uploadedImages() {
      return this.uploadedFiles.filter(
        file => file.type && file.type.startsWith("image/")
      )
    },

    uploadedVideos() {
      return this.uploadedFiles.filter(
        file => file.type && file.type.startsWith("video/")
      )
    },

    uploadedImageIds() {
      return this.uploadedImages.map(file => file.signed_id)
    },

    uploadedVideoUrls() {
      return this.uploadedVideos.map(file => file.url)
    },

    stringifyExtractedUrls() {
      return JSON.stringify(this.extractedUrls)
    },

    visibleUrls() {
      return this.extractedUrls.filter(
        url => !this.ignoredUrls.some(u => urlCompare(u, url))
      )
    },

    visibleAttachments() {
      return this.visibleUrls
        .map(url => {
          const attachment = this.fetchedAttachments.find(a =>
            urlCompare(a.url, url)
          )

          return attachment ? attachment : { url: url }
        })
        .filter(Boolean)
    }
  },

  mounted() {
    const draftData = get(this.draftKey)

    if (draftData) {
      this.title = draftData.title
      this.content = draftData.content
      this.uploadedFiles = draftData.uploadedFiles || []
      this.fetchedAttachments = draftData.fetchedAttachments || []
      this.ignoredUrls = draftData.ignoredUrls || []
    }
  },

  beforeDestroy() {
    if (!this.isDirty) {
      unset(this.draftKey)
    }
  },

  watch: {
    title() {
      this.handleSaveDraft()
    },

    content() {
      if (isEmpty(this.content)) {
        this.extractedUrls = []
        this.ignoredUrls = []
      } else {
        debounce(() => {
          this.extractedUrls = extractUrls(this.content)
        }, 10)
      }

      this.handleSaveDraft()
    },

    stringifyExtractedUrls() {
      // Watch by stringify will improve the performance
      debounce(this.handleFetchAttachment, 100)
    }
  },

  methods: {
    handlePublishPost() {
      this.isPosting = true

      ApiClient.post(`/threads/${this.messageThreadId}/post`, {
        title: this.title,
        content: this.content,
        image_ids: this.uploadedImageIds,
        video_urls: this.uploadedVideoUrls,
        attachment_urls: this.visibleAttachments.map(a => a.url)
      })
        .then(res => {
          this.isSuccess = true
          this.handleClearDraft()
          this.$emit("posted", res.data.message_ts)
        })
        .catch(err => {
          this.$notify(err.response.data.message)
        })
        .finally(() => {
          this.isPosting = false
        })
    },

    handleFetchAttachment() {
      // Only fetch attachments for new urls
      const urlsToFetch = this.extractedUrls.filter(
        url =>
          ![
            ...this.ignoredUrls,
            ...this.fetchedAttachments.map(a => a.url)
          ].some(u => urlCompare(u, url))
      )

      if (urlsToFetch.length < 1) return

      ApiClient.get("/threads/meta_info", {
        params: {
          urls: urlsToFetch
        }
      }).then(({ data }) => {
        this.fetchedAttachments = [
          ...this.fetchedAttachments,
          ...data.attachments
        ]

        this.handleSaveDraft()
      })
    },

    handleSaveDraft() {
      debounce(() => {
        if (this.draftKey) {
          set(this.draftKey, {
            title: this.title,
            content: this.content,
            uploadedFiles: this.uploadedFiles,
            fetchedAttachments: this.fetchedAttachments,
            ignoredUrls: this.ignoredUrls
          })
        }
      }, 100)
    },

    handleClearDraft() {
      this.title = null
      this.content = null
      this.uploadedFiles = []
      this.fetchedAttachments = []
      this.ignoredUrls = []

      unset(this.draftKey)
    },

    handleUploaded(file) {
      this.uploadedFiles.push(file)
      this.handleSaveDraft()
    },

    handleRemoveUploaded(file) {
      this.uploadedFiles = this.uploadedFiles.filter(
        f => f.signed_id != file.signed_id || f.type != file.type
      )
      this.handleSaveDraft()
    },

    handleRemoveAttachment(attachment) {
      this.fetchedAttachments = this.fetchedAttachments.filter(
        a => a.url != attachment.url
      )

      // Removed attachments will no longer be visible even if we rewrite its link in the content
      this.ignoredUrls.push(attachment.url)
      this.handleSaveDraft()
    },

    handleClose() {
      if (this.isDirty) {
        this.showExitModal = true
        this.$router.push("#post-creator")
      } else {
        this.$emit("close")
      }
    }
  }
}
</script>

<style lang="scss">
@import "@/assets/styles/variables";

.post-creator__container {
  .tip-tap-editor {
    display: flex;
    justify-content: space-between;
    flex-direction: column;
  }

  .post-title input {
    font-size: $size--6;
    font-weight: bold;
    color: $color-ui--black;
  }
}
</style>
